import { getDirectorioPublico } from './api.js';
import { escapeHtml } from '../lib/html.js';
import { registerServiceWorker } from '../lib/sw.js';

const app = document.getElementById('app');

// CDN estático de logos — mismo patrón que el launcher principal
// (selector.js:16). ~99% de los centros tienen logo acá. Fallback: el placeholder
// con la inicial del nombre. Mantenemos solo el CDN como fuente porque el
// fallback Arvixe (slot.url/images30/...) implica filtrar más datos del backend
// del directorio público — innecesario para esta vista.
const IMAGES_BASE = 'https://estaticos.tomoturnos.com/staticimages/imagesNew';

// URL de búsqueda genérica en Wellhub — fallback cuando un centro no tiene
// `wellhubUrlPublica` seteado (en proceso de homologación, o pendiente de carga
// manual por Soporte). Lleva al usuario al search de Wellhub con el nombre
// del centro pre-poblado — desde ahí puede confirmar disponibilidad y reservar.
const WELLHUB_SEARCH_BASE = 'https://wellhub.com/es-ar/search/partners';

// Recursos públicos que querés que el usuario tenga a mano. Definidos arriba
// para que sean fáciles de editar sin tocar la lógica.
const RECURSOS = [
  {
    titulo: 'Info general sobre Wellhub',
    descripcion: 'Qué es y cómo funciona — video introductorio.',
    url: 'https://www.youtube.com/watch?v=QZD4Mtx8vNE',
    tipo: 'video'
  },
  {
    titulo: 'Cómo hacer check-in',
    descripcion: 'Paso a paso para registrar tu asistencia el día de la clase.',
    url: 'https://www.youtube.com/watch?v=5N9P3x7-Gbg',
    tipo: 'video'
  },
  {
    titulo: 'Cómo hacer una reserva (Booking)',
    descripcion: 'Tutorial oficial del Help Center de Wellhub.',
    url: 'https://helpcenter.gympass.com/es-es/articles/como-reservar-una-clase',
    tipo: 'articulo'
  },
  {
    titulo: 'Videotutorial (en inglés)',
    descripcion: 'YouTube short — visualización rápida del flujo.',
    url: 'https://www.youtube.com/shorts/mocyGf8U7qc',
    tipo: 'video'
  },
  {
    titulo: '¿Qué ocurre si olvidás hacer check-in o cancelás tarde?',
    descripcion: 'Reglas y consecuencias — artículo oficial.',
    url: 'https://helpcenter.gympass.com/es-ar/articles/que-ocurre-si-olvidas-realizar-un-check-in-o-cancelas-tarde-una-clase',
    tipo: 'articulo'
  }
];

// Construye el `<img>` del logo. Patrón copiado del launcher principal
// (selector.js:23-34) — CDN estático con onerror que oculta el `<img>` para
// que muestre el placeholder con la inicial detrás.
function logoImg(centro) {
  const alias = escapeHtml(centro.alias || '');
  const nombre = escapeHtml(centro.nombre || centro.alias || '');
  if (!alias) return '';
  const cdnUrl = `${IMAGES_BASE}/${alias}/logo_fix.jpg`;
  return `<img src="${cdnUrl}" class="centro-logo" alt="Logo ${nombre}" loading="lazy" onerror="this.style.display='none'" />`;
}

// Placeholder visual cuando el logo no carga: círculo con la inicial.
function logoPlaceholder(centro) {
  const inicial = ((centro.nombre || centro.alias || '?').trim().charAt(0) || '?').toUpperCase();
  return `<span class="centro-logo-fallback" aria-hidden="true">${escapeHtml(inicial)}</span>`;
}

// Construye la URL al espacio público del centro en Wellhub. Si el slug está
// seteado, link directo; si no, fallback al search de Wellhub con el nombre
// del centro como query — el visitante igualmente puede accionar.
//
// Sanitización defensiva del slug: trim de espacios + strip de `/` al inicio
// y al final. Si no, slugs como "ap-pilates-estudio-1-saavedra/" (que algunos
// admins copian con el slash final del URL de Wellhub) generan
// `.../partners/ap-pilates-estudio-1-saavedra%2F/` (encodeURIComponent escapa
// el `/` interno) y Wellhub responde 404. El backend también normaliza al
// guardar (`WellhubService`), esto es cinturón + tirantes para slugs viejos
// que se persistieron antes del fix.
function urlWellhub(slug, nombreFallback) {
  if (slug) {
    const limpio = String(slug).trim().replace(/^\/+|\/+$/g, '');
    if (limpio) {
      return `https://wellhub.com/es-ar/search/partners/${encodeURIComponent(limpio)}/`;
    }
  }
  return `${WELLHUB_SEARCH_BASE}?query=${encodeURIComponent(nombreFallback || '')}`;
}

// Agrupa centros por (slotIdx, idEmpresa). En multi-gym el backend emite N
// filas (una por gym) — visualmente queremos UNA card por empresa con N CTAs
// de sede adentro, no N cards repetidas con el mismo logo.
function agruparPorEmpresa(lista) {
  const groups = new Map();
  for (const c of lista) {
    const key = `${c.slotIdx}_${c.idEmpresa}`;
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        slotIdx: c.slotIdx,
        idEmpresa: c.idEmpresa,
        alias: c.alias,
        nombre: c.nombre,
        // estado del grupo: homologada si AL MENOS una sede lo está. Si todas
        // están en proceso, el grupo también.
        estado: 'en_proceso',
        // fueguito a nivel empresa: prende si CUALQUIER sede es "activaCaliente"
        // según el backend (ratio check-ins/alumno últimos 30d ≥ umbral).
        esActivaCaliente: false,
        sedes: []
      });
    }
    const g = groups.get(key);
    g.sedes.push({
      nombreSede: c.nombreSede,
      wellhubUrlPublica: c.wellhubUrlPublica,
      estado: c.estado
    });
    if (c.estado === 'homologada') g.estado = 'homologada';
    if (c.esActivaCaliente) g.esActivaCaliente = true;
  }
  return Array.from(groups.values());
}

// Estado en memoria — pequeño, no necesita store. Los handlers leen y escriben
// directo sobre estas vars. Filtra por nombre con `normalize` (sin acentos, lower)
// para que "olivia" matchee "Olivia Pilates Insieme".
const state = {
  centros: [],
  loading: true,
  failedSlots: [],
  query: ''
};

function normalize(s) {
  return (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function centrosFiltrados() {
  const q = normalize(state.query.trim());
  if (!q) return state.centros;
  return state.centros.filter(c =>
    normalize(c.nombre).includes(q)
    || normalize(c.alias).includes(q)
    || normalize(c.nombreSede || '').includes(q)
  );
}

function renderShell() {
  app.className = '';
  app.innerHTML = `
    <header class="topbar">
      <a href="/" class="topbar-logo-link" aria-label="Ir al launcher de TomoTurnos">
        <img src="/tomoturnos-logo.png" alt="TomoTurnos" class="topbar-logo" />
      </a>
    </header>
    <div class="selector-headline">
      <h2>Centros disponibles en <em>Wellhub</em></h2>
      <p>Encuentra el centro al que querés ir, accede a su espacio en Wellhub y reservá tu clase.</p>
    </div>
    <section class="wh-search-wrap">
      <div class="search-box">
        <input
          type="search"
          id="search-input"
          placeholder="Busca un centro…"
          autocomplete="off"
          autocapitalize="off"
          autocorrect="off"
          spellcheck="false"
          inputmode="search"
          aria-label="Buscar centro"
        />
        <button id="search-clear" type="button" class="search-clear" hidden aria-label="Limpiar">×</button>
      </div>
    </section>
    <div id="wh-status" class="wh-status"></div>
    <section id="wh-directorio" class="wh-directorio" aria-live="polite"></section>
    <section id="wh-recursos" class="wh-recursos"></section>
    <footer class="panel-footer">
      <a href="/">← Volver al launcher</a>
      <span class="panel-footer-sep" aria-hidden="true">·</span>
      <a href="https://wellhub.com" target="_blank" rel="noopener">Ir a Wellhub →</a>
    </footer>
  `;

  const input = document.getElementById('search-input');
  const clearBtn = document.getElementById('search-clear');
  input.addEventListener('input', () => {
    state.query = input.value;
    clearBtn.hidden = input.value.length === 0;
    renderDirectorio();
  });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      input.value = '';
      state.query = '';
      clearBtn.hidden = true;
      renderDirectorio();
    }
  });
  clearBtn.addEventListener('click', () => {
    input.value = '';
    state.query = '';
    clearBtn.hidden = true;
    input.focus();
    renderDirectorio();
  });

  // Foco automático al input (consistente con el launcher principal).
  setTimeout(() => input.focus(), 50);
}

function renderStatus() {
  const el = document.getElementById('wh-status');
  if (!el) return;
  if (state.loading) {
    el.innerHTML = `<p class="wh-loading">Cargando directorio…</p>`;
    return;
  }
  // Banner solo si algún slot falló — la UX sigue funcional con los demás.
  if (state.failedSlots.length > 0) {
    el.innerHTML = `
      <div class="banner banner-warn" role="status">
        <strong>Algunos servidores no respondieron.</strong>
        Es posible que falten algunos centros. <a href="#" id="wh-retry">Reintentar</a>.
      </div>`;
    const retry = document.getElementById('wh-retry');
    if (retry) retry.addEventListener('click', (e) => { e.preventDefault(); cargarDirectorio(); });
    return;
  }
  el.innerHTML = '';
}

function renderTarjeta(g) {
  const nombre = escapeHtml(g.nombre || g.alias);
  const esHomologada = g.estado === 'homologada';
  const multiSede = g.sedes.length > 1;

  // UI unificada (2026-06-05): tanto single como multi-sede usan el mismo
  // patrón de filas. Cada fila = label (sede o empresa) + "Reservar →" en
  // bloque verde a la derecha. Multi-sede agrega el header "Elegí una sede".
  // Single-sede emite 1 sola fila con el nombre de la empresa.
  const buildRow = (label, href, sedeHomologada) => {
    const ctaTitle = sedeHomologada ? 'Reservar en Wellhub' : 'Buscar en Wellhub';
    return `<a class="wh-sede-row" href="${escapeHtml(href)}" target="_blank" rel="noopener" title="${ctaTitle}">
      <span class="wh-sede-name">${escapeHtml(label)}</span>
      <span class="wh-sede-cta">Reservar <span class="wh-sede-arrow" aria-hidden="true">→</span></span>
    </a>`;
  };

  let rowsHtml;
  if (multiSede) {
    const items = g.sedes.map(s =>
      buildRow(s.nombreSede || 'Sede', urlWellhub(s.wellhubUrlPublica, g.nombre), s.estado === 'homologada')
    ).join('');
    rowsHtml = `<div class="wh-card-sedes">
      <div class="wh-sedes-label">Elegí una sede:</div>
      ${items}
    </div>`;
  } else {
    const s = g.sedes[0];
    rowsHtml = `<div class="wh-card-sedes wh-card-sedes-single">
      ${buildRow(g.nombre || g.alias, urlWellhub(s.wellhubUrlPublica, g.nombre), esHomologada)}
    </div>`;
  }

  // Fueguito: solo aplica a homologadas (en_proceso por definición no tiene
  // actividad). Posición: top-left del logo bandeau — visible al instante sin
  // robar espacio del nombre. Tooltip explica qué significa para FOMO + para
  // que Wellhub-corp/admins entiendan el indicador al ver la URL.
  const fueguitoHtml = (esHomologada && g.esActivaCaliente)
    ? `<span class="wh-fueguito" title="Integración con mucha actividad — alumnos reservando y haciendo check-in regularmente" aria-label="Integración muy activa">🔥</span>`
    : '';

  return `
    <article class="wh-card">
      <div class="wh-card-logo">
        ${logoPlaceholder(g)}
        ${logoImg(g)}
        ${fueguitoHtml}
      </div>
      <div class="wh-card-body">
        ${multiSede ? `<h3 class="wh-card-nombre">${nombre}</h3>` : ''}
        ${esHomologada ? '' : `<p class="wh-card-nota">Este centro todavía está completando la homologación con Wellhub. Podés buscarlo en Wellhub para verificar disponibilidad.</p>`}
        ${rowsHtml}
      </div>
    </article>
  `;
}

function renderDirectorio() {
  const el = document.getElementById('wh-directorio');
  if (!el) return;
  if (state.loading) {
    el.innerHTML = '';
    return;
  }
  const lista = centrosFiltrados();
  if (state.centros.length === 0 && state.failedSlots.length > 0) {
    el.innerHTML = `<p class="wh-empty">No pudimos cargar el directorio. Reintentá en unos minutos.</p>`;
    return;
  }
  if (lista.length === 0 && state.query) {
    el.innerHTML = `<p class="wh-empty">Sin resultados para "${escapeHtml(state.query)}".</p>`;
    return;
  }
  if (lista.length === 0) {
    el.innerHTML = `<p class="wh-empty">No hay centros en el directorio todavía.</p>`;
    return;
  }
  // Agrupar por empresa (slotIdx + idEmpresa): una empresa multi-gym emite N
  // filas en el backend pero acá la mostramos como UNA card con N CTAs de sede.
  const grupos = agruparPorEmpresa(lista);

  // Split en homologadas (al menos una sede homologada) y en_proceso (todas
  // las sedes en proceso). Dentro de homologadas, las 🔥 calientes van primero
  // (anzuelo visual al tope del grid) y después el resto alfabético.
  const homologadas = grupos.filter(g => g.estado === 'homologada').sort((a, b) => {
    if (a.esActivaCaliente !== b.esActivaCaliente) return a.esActivaCaliente ? -1 : 1;
    return (a.nombre || '').localeCompare(b.nombre || '');
  });
  const enProceso   = grupos.filter(g => g.estado === 'en_proceso');

  let html = '';
  if (homologadas.length > 0) {
    html += `<h3 class="wh-section-title">Disponibles en Wellhub <small>(${homologadas.length})</small></h3>`;
    html += `<div class="wh-grid">${homologadas.map(renderTarjeta).join('')}</div>`;
  }
  if (enProceso.length > 0) {
    html += `<h3 class="wh-section-title wh-section-title-secondary">En proceso de homologación <small>(${enProceso.length})</small></h3>`;
    html += `<p class="wh-section-desc">Estos centros ya activaron Wellhub en TomoTurnos pero aún están completando la homologación del lado del partner. Si te interesa alguno, podés buscarlo en Wellhub para ver disponibilidad.</p>`;
    html += `<div class="wh-grid">${enProceso.map(renderTarjeta).join('')}</div>`;
  }
  el.innerHTML = html;
}

function renderRecursos() {
  const el = document.getElementById('wh-recursos');
  if (!el) return;
  const items = RECURSOS.map(r => `
    <a class="wh-recurso" href="${escapeHtml(r.url)}" target="_blank" rel="noopener">
      <span class="wh-recurso-icon" aria-hidden="true">${r.tipo === 'video' ? '▶' : '📖'}</span>
      <span class="wh-recurso-body">
        <strong>${escapeHtml(r.titulo)}</strong>
        <span class="wh-recurso-desc">${escapeHtml(r.descripcion)}</span>
      </span>
    </a>
  `).join('');
  el.innerHTML = `
    <h3 class="wh-section-title">Aprendé a usar Wellhub</h3>
    <p class="wh-section-desc">Recursos para alumnos y admins — videos y artículos del Help Center oficial.</p>
    <div class="wh-recursos-list">${items}</div>
  `;
}

async function cargarDirectorio() {
  state.loading = true;
  state.failedSlots = [];
  renderStatus();
  renderDirectorio();
  try {
    const result = await getDirectorioPublico();
    state.centros = result.centros;
    state.failedSlots = result.failedSlots;
  } catch (err) {
    console.error('[wellhub] error cargando directorio', err);
    state.centros = [];
    state.failedSlots = [{ url: '?', message: err && err.message ? err.message : String(err) }];
  } finally {
    state.loading = false;
    renderStatus();
    renderDirectorio();
  }
}

function bootstrap() {
  // Mismo SW que el launcher (scope '/'): registrarlo también acá cubre al usuario
  // que llega directo a /wellhub sin pasar por el selector. Ver src/lib/sw.js.
  registerServiceWorker();
  renderShell();
  renderRecursos();
  cargarDirectorio();
}

bootstrap();
